import { Link } from "react-router-dom";
import type { ComponentProps } from "react";

import { EXTERNAL_PATHS } from "../data/nav";

type Props = Omit<ComponentProps<"a">, "href"> & {
  href: string;
  /** Force a full page load even for a same-origin path. */
  hard?: boolean;
};

/**
 * One link component for the whole site, because there are three kinds of
 * destination here and getting them mixed up breaks things quietly.
 *
 *   1. React routes            -> <Link>, no page reload
 *   2. Absolute URLs, mailto,  -> <a target="_blank">
 *      tel, hash-only anchors
 *   3. /hazribook              -> <a> with a REAL navigation
 *
 * (3) is the one that matters. /hazribook is a hand-written static page in
 * public/ with its own design system. If the router claims it, the React
 * app answers and 404s inside its own router — the exact failure the
 * project's _redirects rule exists to prevent. Routing it through <Link>
 * would reintroduce that bug above the CDN, where _redirects cannot help.
 */
export function SmartLink({ href, hard, children, ...rest }: Props) {
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
  const isHash = href.startsWith("#");
  const isStaticPage = EXTERNAL_PATHS.some(
    (p) => href === p || href.startsWith(`${p}/`) || href.startsWith(`${p}#`),
  );

  if (isAbsolute || isHash || isStaticPage || hard) {
    const external = isAbsolute && !href.startsWith("mailto:") && !href.startsWith("tel:");
    return (
      <a
        href={href}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}
